import { Board } from "./board";
import { legalMoves, applyMove } from "./moves";
import { PLAYER_BOTTOM_ROW, STALL_PENALTY_TURNS, WRAP_SPAWN_MAX, WRAP_SPAWN_MIN } from "./constants";
import type { Piece, Square } from "./types";
import { randInt } from "./rng";

export interface WrapResult {
  wrapped: boolean;
  spawned: Piece[];
}

/**
 * If the player-side bottom row is empty, shift the whole board down by one
 * (bottom row drops off, a new empty row appears at the top), and spawn
 * 1-2 random enemy pieces into the new top row.
 */
export function maybeWrap(board: Board, rng: () => number): WrapResult {
  if (!board.rowIsEmpty(PLAYER_BOTTOM_ROW)) return { wrapped: false, spawned: [] };
  board.shiftDownOne();
  const minSpawn = Math.min(WRAP_SPAWN_MIN, WRAP_SPAWN_MAX);
  const maxSpawn = Math.max(WRAP_SPAWN_MIN, WRAP_SPAWN_MAX);
  const spawnCount = minSpawn + randInt(rng, maxSpawn - minSpawn + 1);
  const spawned = board.spawnEnemiesAnywhere(rng, spawnCount);
  return { wrapped: true, spawned };
}

/**
 * Anti-camp penalty: flat cadence of one enemy piece for every STALL_PENALTY_TURNS
 * player moves that pass without a wrap. Returns how many enemies to spawn this move.
 * The caller resets its move counter to 0 whenever a wrap occurs.
 */
export function stallPenaltyCount(movesSinceWrap: number): number {
  return movesSinceWrap > 0 && movesSinceWrap % STALL_PENALTY_TURNS === 0 ? 1 : 0;
}

export interface EnemyAction {
  from: Square;
  to: Square;
  captured: Piece | null;
}

/** Pick one random legal enemy move and apply it. Returns null if no enemy can move. */
export function performRandomEnemyMove(board: Board, rng: () => number): EnemyAction | null {
  const candidates: { from: Square; moves: Square[] }[] = [];
  for (const { sq } of board.squaresOf("enemy")) {
    const moves = legalMoves(board, sq);
    if (moves.length > 0) candidates.push({ from: sq, moves });
  }
  if (candidates.length === 0) return null;
  const choice = candidates[randInt(rng, candidates.length)];
  const to = choice.moves[randInt(rng, choice.moves.length)];
  const captured = applyMove(board, choice.from, to);
  return { from: choice.from, to, captured };
}
